/**
 * Standfirst — newspaper-style hero block at the top of the Command Center.
 *
 * Composition: kicker pill + 52-60px italic-mixed headline + deck paragraph +
 * byline + Dark Navy ScoreBox alongside (right column on lg+, stacked below
 * on smaller viewports).
 *
 * Visual reference: 04-hybrid-recommended.html .standfirst block.
 * Bottom rule is a 4px DOUBLE NAVY BORDER — a deliberate editorial touch
 * that breaks up the page without resorting to a generic divider.
 */

import type { PostureBrief } from '@/lib/posture-brief'
import ScoreBox from './ScoreBox'

interface Props {
  brief: PostureBrief
  /** Composite score (0-100). */
  score: number | null
  /** Component breakdown for the score box. */
  components: {
    label: string
    weight: string
    value: number
    fillClass: string
  }[]
  isLoading?: boolean
}

/** Render a string with `**bold**` markers as <strong>. Lightweight — no
 *  full markdown parser needed for the deck/byline lines we control. */
function renderEmphasis(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-current">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

export default function Standfirst({ brief, score, components, isLoading }: Props) {
  return (
    <section
      aria-labelledby="standfirst-headline"
      className="
        standfirst-rule
        grid grid-cols-1 gap-10 pb-10 pt-2
        lg:grid-cols-[1.5fr_1fr] lg:gap-14
      "
    >
      <div className="lg:pr-6">
        <span
          className="
            inline-flex items-center gap-2.5 rounded-[2px] bg-[#212938] px-3.5 py-1.5
            text-[10px] font-bold uppercase tracking-[0.28em] text-white
          "
        >
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: 'rgb(var(--color-primary-500))',
              boxShadow: '0 0 6px rgb(var(--color-primary-500))',
            }}
          />
          {brief.kicker}
        </span>

        <h1
          id="standfirst-headline"
          className="mt-5 font-display font-extrabold tracking-[-0.025em]"
          style={{
            fontSize: 'clamp(36px, 5vw, 60px)',
            lineHeight: 0.96,
            // --color-text-primary swaps with the theme: slate-100 in dark,
            // slate-900 (close to brand navy) in light. Using the var directly
            // sidesteps Tailwind's slate utility (which doesn't route through
            // our theme variables).
            color: 'rgb(var(--color-text-primary))',
          }}
        >
          {brief.headlineSubject}{' '}
          <em
            style={{
              fontStyle: 'italic',
              fontWeight: 600,
              // primary-700 is the deeper green that reads on both surfaces.
              color: 'rgb(var(--color-primary-700))',
            }}
          >
            {brief.headlineModifier}
          </em>
          {/* Period closes the first sentence; weakLink renders as a complete
              second sentence on the next line. Avoids the AI-coded em-dash. */}
          .
          <br />
          {brief.weakLink}
        </h1>

        <p
          className="mt-5 max-w-xl text-[16px] leading-[1.55]"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          {renderEmphasis(brief.deck)}
        </p>

        <div
          className="
            mt-6 flex flex-wrap gap-4 border-t pt-3.5
            text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500
          "
          style={{ borderTopColor: 'rgb(var(--color-dark-600))' }}
        >
          {brief.byline.split(' · ').map((segment, i, arr) => (
            <span key={i} className="contents">
              <span>{renderEmphasis(segment)}</span>
              {i < arr.length - 1 && <span aria-hidden>·</span>}
            </span>
          ))}
        </div>
      </div>

      <ScoreBox
        score={score}
        verdict={brief.verdict}
        delta={brief.delta}
        components={components}
        isLoading={isLoading}
      />
    </section>
  )
}
